/**
 * Display formatting.
 *
 * Money arrives from the server as an exact decimal string. These helpers format
 * it for display without ever parsing it into a JS number, because a float64
 * cannot hold an eight-decimal crypto balance exactly and a wallet that shows a
 * rounded figure is a support ticket waiting to happen.
 */

/** 250 -> "2.50x" */
export function formatMultiplier(x100: number): string {
  const whole = Math.floor(x100 / 100);
  const cents = Math.abs(x100 % 100);
  return `${whole}.${cents.toString().padStart(2, '0')}x`;
}

/**
 * Formats a decimal string to a fixed number of places, by string surgery.
 *
 * It truncates, and that is a display decision rather than an echo of the
 * server's arithmetic -- the server rounds nothing off a payout any more, so a
 * balance can hold more precision than a currency shows. A wallet of 10.999
 * would read "11.00" if this rounded, and 11.00 is not a stake that wallet can
 * place. What is on screen has to be money the player can actually use; the
 * remainder is not lost, it is in the balance waiting to become a whole unit.
 */
export function formatAmount(value: string, scale: number): string {
  const negative = value.startsWith('-');
  const unsigned = negative ? value.slice(1) : value;

  const dot = unsigned.indexOf('.');
  const whole = dot === -1 ? unsigned : unsigned.slice(0, dot);
  const fraction = dot === -1 ? '' : unsigned.slice(dot + 1);

  const padded = scale === 0 ? '' : `.${fraction.slice(0, scale).padEnd(scale, '0')}`;
  const grouped = groupThousands(whole || '0');

  return `${negative ? '-' : ''}${grouped}${padded}`;
}

export function formatMoney(value: string, scale: number, symbol: string): string {
  const { amount, unit, unitFirst } = formatMoneyParts(value, scale, symbol);
  return unitFirst ? `${unit}${amount}` : `${amount} ${unit}`;
}

/**
 * The same, except that decimals beyond the currency's scale are kept when the
 * value has them.
 *
 * This exists for exactly one thing: telling a player what a cash-out paid. A
 * 0.10 stake at 1.01x pays 0.101, and the server credits all three places -- so
 * "you won 0.10" would be the interface hiding a win that the wallet took. At
 * two places the figure reads as before ("23.50"); it only grows when there is
 * something to show.
 *
 * A balance is deliberately *not* formatted this way. A payout is an event, and
 * its exact size is the point; a balance is a spending limit, and 0.001 of it is
 * not spendable. A statement screen, when there is one, belongs on this side of
 * that line too: a ledger that rounds is a ledger that does not add up.
 */
export function formatMoneyExact(value: string, scale: number, symbol: string): string {
  const dot = value.indexOf('.');
  // Trailing zeros carry no information past the currency's own scale.
  const decimals = dot === -1 ? 0 : value.slice(dot + 1).replace(/0+$/, '').length;
  return formatMoney(value, Math.max(scale, decimals), symbol);
}

/**
 * The same figure split into its parts, for a caller that styles them
 * differently -- the balance in the header draws the number in full strength and
 * the currency muted beside it.
 *
 * It exists so the rule for which side the unit goes on lives in one place. Two
 * callers deciding that independently is how a header ends up reading "$10.00"
 * while the panel under it reads "10.00 $".
 */
export function formatMoneyParts(
  value: string,
  scale: number,
  symbol: string,
): { amount: string; unit: string; unitFirst: boolean } {
  // Currency codes read better after the figure, symbols before it.
  return { amount: formatAmount(value, scale), unit: symbol, unitFirst: symbol.length <= 2 };
}

function groupThousands(digits: string): string {
  let out = '';
  for (let i = 0; i < digits.length; i += 1) {
    const fromEnd = digits.length - i;
    out += digits[i];
    if (fromEnd > 1 && fromEnd % 3 === 1) out += ' ';
  }
  return out;
}

/**
 * A decimal string as a whole number of the currency's smallest units.
 *
 * This is how the quick-stake buttons are derived: "the minimum times two" has
 * to be exact for a currency with eight decimal places, and multiplying the
 * decimal string as a float would produce 0.30000000000000004 on a currency
 * where that is a different amount of money. Integer units multiply exactly.
 *
 * It is a display helper only -- nothing here is ever sent as an amount; the
 * canonical decimal string is. Returns null for anything unparseable.
 */
export function toUnits(value: string, scale: number): number | null {
  const cleaned = value.trim().replace(',', '.');
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;

  const [whole = '0', fraction = ''] = cleaned.split('.');
  const padded = fraction.slice(0, scale).padEnd(scale, '0');
  const units = Number(`${whole || '0'}${padded}`);
  return Number.isSafeInteger(units) ? units : null;
}

/** The inverse of toUnits: 30 at scale 2 -> "0.30". */
export function fromUnits(units: number, scale: number): string {
  const sign = units < 0 ? '-' : '';
  const digits = Math.abs(Math.trunc(units)).toString().padStart(scale + 1, '0');
  if (scale === 0) return `${sign}${digits}`;
  return `${sign}${digits.slice(0, -scale)}.${digits.slice(-scale)}`;
}

/** Parses a stake typed by a player into a canonical decimal string, or null. */
export function normaliseStake(input: string, scale: number): string | null {
  const cleaned = input.trim().replace(',', '.');
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.') return null;

  const [whole = '0', fraction = ''] = cleaned.split('.');
  const truncated = fraction.slice(0, scale);
  const value = scale === 0 || truncated === '' ? whole : `${whole}.${truncated}`;

  // Reject a zero stake here so the server does not have to.
  if (/^0*\.?0*$/.test(value)) return null;
  return value;
}

/** "1.30" or "1.3" -> 130. Returns 0 for anything unusable. */
export function parseMultiplierToX100(input: string): number {
  const cleaned = input.trim().replace(',', '.');
  if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '') return 0;

  const [whole = '0', fraction = ''] = cleaned.split('.');
  const cents = fraction.slice(0, 2).padEnd(2, '0');
  const value = Number(whole) * 100 + Number(cents || 0);
  return Number.isFinite(value) ? value : 0;
}

export function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}
